import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { toPaletteColorName } from '@/features/colors/lib/paletteLookup';
import { PALETTE_COLOR_NAMES } from '@/features/colors/palettes';
import { factory } from '@/features/eventMetadata/components/CategorySelect/categoryLib';
import { useCreateCategory } from '@/features/eventMetadata/hooks/useCreateCategory';
import { useUpdateCategorySortOrder } from '@/features/eventMetadata/hooks/useUpdateCategorySortOrder';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import type { ExperienceCategory } from '@/types/gql.generated';
import { Button, Flex, Icon, Text } from '@/ui';
import { SidebarSection } from '../../SidebarKit';
import { ColorCategoryRow } from './ColorCategoryRow';

const bySortOrder = (a: ExperienceCategory, b: ExperienceCategory) =>
  a.sortOrder - b.sortOrder;

export const ColorCategoriesSection = () => {
  const { experience } = usePageExperienceContext();
  const { createCategory } = useCreateCategory();
  const { updateSortOrder } = useUpdateCategorySortOrder();

  // Local order for optimistic drag-and-drop; re-synced when the source changes.
  const [categories, setCategories] = useState(() =>
    [...experience.categories].sort(bySortOrder)
  );
  useUpdateEffect(() => {
    setCategories([...experience.categories].sort(bySortOrder));
  }, [experience.categories]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Move 5px before activating DnD so the handle can still be clicked.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = categories.findIndex(({ id }) => id === active.id);
    const newIndex = categories.findIndex(({ id }) => id === over.id);
    const previous = categories;
    const reordered = arrayMove(categories, oldIndex, newIndex);
    setCategories(reordered);
    // Roll the optimistic order back if the server rejects the change.
    updateSortOrder(reordered, { onError: () => setCategories(previous) });
  };

  const handleAddColorCategory = () => {
    const usedColorNames = new Set(
      categories.map((c) => toPaletteColorName(c.colorName))
    );
    const next =
      PALETTE_COLOR_NAMES.find((colorName) => !usedColorNames.has(colorName)) ??
      PALETTE_COLOR_NAMES[0];
    createCategory(factory({ colorName: next }));
  };

  return (
    <SidebarSection label="Colors">
      <Text color="ink.muted" fontSize="ah-status" lineHeight={1.5} mb={3}>
        Color categories assigned to events. The chip fill (pastel or bold) is
        set in Style.
      </Text>

      <DndContext
        collisionDetection={closestCenter}
        sensors={sensors}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={categories}
          strategy={verticalListSortingStrategy}
        >
          <Flex direction="column" gap={2} mb={3}>
            {categories.map((category) => (
              <ColorCategoryRow category={category} key={category.id} />
            ))}
          </Flex>
        </SortableContext>
      </DndContext>

      <Button
        leftIcon={<Icon icon="Plus" size={4} />}
        size="ah"
        variant="ahAccentGhost"
        onClick={handleAddColorCategory}
      >
        Add a color category
      </Button>
    </SidebarSection>
  );
};
